import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  createCoreLanguage,
  listCoreLanguages,
  setDefaultLanguage,
  updateCoreLanguage,
  type CoreLanguage,
} from '@/lib/coreSystem';
import Modal from '@/pages/dashboard/core/components/Modal';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

interface FormState { code: string; label: string; nativeLabel: string; flag: string; dir: string; }
const EMPTY: FormState = { code: '', label: '', nativeLabel: '', flag: '', dir: 'ltr' };

export default function LanguagesTab({ org }: Props) {
  const [languages, setLanguages] = useState<CoreLanguage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setLanguages(await listCoreLanguages());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (l: CoreLanguage) => {
    setBusyId(l.id);
    try { await updateCoreLanguage(l.id, { active: l.active ? 0 : 1 }); load(); } finally { setBusyId(null); }
  };

  const makeDefault = async (l: CoreLanguage) => {
    setBusyId(l.id);
    try { await setDefaultLanguage(l.id); load(); } finally { setBusyId(null); }
  };

  const save = async () => {
    if (!form.code.trim() || !form.label.trim()) return;
    setSaving(true);
    try {
      await createCoreLanguage({
        code: form.code.trim().toLowerCase(), label: form.label.trim(),
        nativeLabel: form.nativeLabel.trim() || form.label.trim(), flag: form.flag.trim(), dir: form.dir,
      });
      setOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <p className="text-xs text-foreground-500 max-w-2xl">
          Zifek est internationalisable (français, anglais, arabe…). Les données traduisibles gardent plusieurs versions
          (fr/en/ar) sans dupliquer l'entité entière — la version est stockée dans un champ de traduction.
        </p>
        <button onClick={() => { setForm(EMPTY); setOpen(true); }} className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
          <i className="ri-add-line"></i> Nouvelle langue
        </button>
      </div>

      {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {languages.map((l) => (
            <div key={l.id} className="bg-background-50 border border-background-200/70 rounded-lg p-4 flex flex-col">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-11 h-11 rounded-full bg-background-100 flex items-center justify-center text-2xl">{l.flag || '🌐'}</span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground-900 truncate">{l.label}</p>
                  <p className="text-xs text-foreground-500 truncate">{l.native_label} · {l.code.toUpperCase()}</p>
                </div>
                {l.is_default ? (
                  <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 whitespace-nowrap">Par défaut</span>
                ) : null}
              </div>
              <div className="flex items-center gap-2 mt-auto pt-3 border-t border-background-200/60">
                <span className="inline-flex items-center gap-1 text-[11px] text-foreground-500 whitespace-nowrap"><i className="ri-arrow-left-right-line"></i>{l.dir === 'rtl' ? 'Droite→Gauche' : 'Gauche→Droite'}</span>
                <div className="ml-auto flex items-center gap-1">
                  {!l.is_default && (
                    <button onClick={() => makeDefault(l)} disabled={busyId === l.id} className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">Définir défaut</button>
                  )}
                  <button
                    onClick={() => toggleActive(l)}
                    disabled={busyId === l.id || !!l.is_default}
                    className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer disabled:opacity-50 ${l.active ? 'bg-primary-500' : 'bg-background-200'}`}
                    title={l.is_default ? 'La langue par défaut reste active' : ''}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 transition-all ${l.active ? 'left-[22px]' : 'left-0.5'}`}></span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-5 p-4 bg-secondary-50 border border-secondary-200 rounded-lg flex items-start gap-3">
        <i className="ri-information-line text-secondary-700 text-lg mt-0.5"></i>
        <p className="text-xs text-secondary-900">
          Exemple de donnée traduisible : <span className="font-semibold">service name</span> → fr « Consultation », en « Consultation », ar « استشارة ».
          Un seul enregistrement, plusieurs versions linguistiques.
        </p>
      </div>

      {open && (
        <Modal
          title="Nouvelle langue"
          onClose={() => setOpen(false)}
          maxWidth="max-w-lg"
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={save} disabled={saving || !form.code.trim() || !form.label.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Enregistrer
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Code</span>
                <input className={INPUT} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Ex : es" />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Drapeau (emoji)</span>
                <input className={INPUT} value={form.flag} onChange={(e) => setForm({ ...form, flag: e.target.value })} placeholder="🇪🇸" />
              </label>
            </div>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Libellé</span>
              <input className={INPUT} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Ex : Espagnol" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Nom natif</span>
              <input className={INPUT} value={form.nativeLabel} onChange={(e) => setForm({ ...form, nativeLabel: e.target.value })} placeholder="Ex : Español" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Sens d'écriture</span>
              <select className={INPUT} value={form.dir} onChange={(e) => setForm({ ...form, dir: e.target.value })}>
                <option value="ltr">Gauche → Droite</option>
                <option value="rtl">Droite → Gauche</option>
              </select>
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
}